/**
 * CSS-subset parser (UI-ENGINE-DESIGN.md §Style system). Runs at IMPORT time (editor) and in
 * tests — the player consumes the parsed {@link StyleSheetData} JSON and never parses CSS.
 * Unknown properties/values produce warnings and are skipped, never fatal.
 *
 * Selector grammar v1: class compounds + pseudo-classes only (`.a.b:hover`), comma lists.
 * No element/descendant selectors, no specificity beyond (class+pseudo count, source order).
 */
import {
  PSEUDO_STATES,
  type AnimationDecl,
  type AnimationDirection,
  type AnimationFillMode,
  type Color4,
  type Dim,
  type DimCalc,
  type EasingName,
  type GradientDecl,
  type KeyframeStop,
  type Keyframes,
  type PseudoState,
  type SelectorCompound,
  type ShadowDecl,
  type StyleDecls,
  type StyleRule,
  type StyleSheetData,
  type TransformDecl,
  type TransformFn,
  type TransformOrigin,
  type TransitionDecl,
  type UIDiagnostic,
} from '../types';

// ---------------------------------------------------------------------------
// value parsers

const NAMED_COLORS: Record<string, Color4> = {
  transparent: [0, 0, 0, 0],
  white: [1, 1, 1, 1],
  black: [0, 0, 0, 1],
  red: [1, 0, 0, 1],
  green: [0, 0.5, 0, 1],
  lime: [0, 1, 0, 1],
  blue: [0, 0, 1, 1],
  yellow: [1, 1, 0, 1],
  cyan: [0, 1, 1, 1],
  magenta: [1, 0, 1, 1],
  gray: [0.5, 0.5, 0.5, 1],
  grey: [0.5, 0.5, 0.5, 1],
  orange: [1, 0.647, 0, 1],
};

export function parseColor(input: string): Color4 | null {
  const s = input.trim().toLowerCase();
  const named = NAMED_COLORS[s];
  if (named) return named;

  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (!/^[0-9a-f]+$/.test(hex)) return null;
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(hex[0]! + hex[0], 16) / 255;
      const g = parseInt(hex[1]! + hex[1], 16) / 255;
      const b = parseInt(hex[2]! + hex[2], 16) / 255;
      const a = hex.length === 4 ? parseInt(hex[3]! + hex[3], 16) / 255 : 1;
      return [r, g, b, a];
    }
    if (hex.length === 6 || hex.length === 8) {
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
      return [r, g, b, a];
    }
    return null;
  }

  const fn = /^rgba?\(([^)]*)\)$/.exec(s);
  if (fn) {
    const parts = fn[1]!.split(',').map((p) => p.trim());
    if (parts.length !== 3 && parts.length !== 4) return null;
    const r = Number(parts[0]) / 255;
    const g = Number(parts[1]) / 255;
    const b = Number(parts[2]) / 255;
    const a = parts.length === 4 ? Number(parts[3]) : 1;
    if ([r, g, b, a].some((v) => !Number.isFinite(v))) return null;
    return [r, g, b, a];
  }
  return null;
}

type UnitVector = { px?: number; pct?: number; vw?: number; vh?: number; em?: number };

/** Collapse a unit vector to the leanest Dim form (plain px / plain % / calc combo). */
function vectorToDim(v: UnitVector): Dim {
  const keys = (Object.keys(v) as (keyof UnitVector)[]).filter((k) => v[k] !== 0 && v[k] !== undefined);
  if (keys.length === 0) return 0;
  if (keys.length === 1 && keys[0] === 'px') return v.px!;
  if (keys.length === 1 && keys[0] === 'pct') return { pct: v.pct! };
  const calc: DimCalc['calc'] = {};
  for (const k of keys) calc[k] = v[k];
  return { calc };
}

/** One `<number><unit>` token → unit vector; bare numbers are px (house default). */
function parseUnitValue(token: string): UnitVector | null {
  const m = /^(-?(?:\d+\.?\d*|\.\d+))(px|%|em|vw|vh)?$/.exec(token.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  switch (m[2]) {
    case '%':
      return { pct: n };
    case 'em':
      return { em: n };
    case 'vw':
      return { vw: n };
    case 'vh':
      return { vh: n };
    default:
      return { px: n };
  }
}

/**
 * `calc()` expression → linear unit vector. Supported grammar (CSS subset): `+`/`-` between
 * dimension terms, `*`/`/` where one side is a plain number, parentheses/nested calc(). All
 * units are linear, so any valid expression normalizes to one vector.
 */
function parseCalc(expr: string): UnitVector | null {
  // tokenize: numbers-with-units, operators, parens ('calc(' collapses to '(')
  const src = expr.replace(/calc\(/gi, '(');
  const tokens = src.match(/-?(?:\d+\.?\d*|\.\d+)(?:px|%|em|vw|vh)?|[+\-*/()]/g);
  if (!tokens) return null;
  let i = 0;

  // scalar (dimensionless) values ride the vector as {px} with a marker — track separately
  type Val = { vec: UnitVector; scalar: boolean };
  const num = (v: UnitVector): number | null => {
    const ks = Object.keys(v) as (keyof UnitVector)[];
    return ks.length === 1 && ks[0] === 'px' ? (v.px ?? 0) : null;
  };

  const factor = (): Val | null => {
    const t = tokens[i];
    if (t === '(') {
      i++;
      const inner = addSub();
      if (!inner || tokens[i] !== ')') return null;
      i++;
      return inner;
    }
    if (t === undefined) return null;
    // bare '-x' handled by the number regex; standalone operators fail here
    const v = parseUnitValue(t);
    if (!v) return null;
    i++;
    const scalar = /^-?(?:\d+\.?\d*|\.\d+)$/.test(t);
    return { vec: v, scalar };
  };

  const mulDiv = (): Val | null => {
    let left = factor();
    while (left && (tokens[i] === '*' || tokens[i] === '/')) {
      const op = tokens[i++];
      const right = factor();
      if (!right) return null;
      if (op === '*') {
        const k = right.scalar ? num(right.vec) : left.scalar ? num(left.vec) : null;
        const dim = right.scalar ? left.vec : right.vec;
        if (k === null) return null; // dimension × dimension is invalid
        const out: UnitVector = {};
        for (const key of Object.keys(dim) as (keyof UnitVector)[]) out[key] = (dim[key] ?? 0) * k;
        left = { vec: out, scalar: left.scalar && right.scalar };
      } else {
        const k = right.scalar ? num(right.vec) : null;
        if (k === null || k === 0) return null;
        const out: UnitVector = {};
        for (const key of Object.keys(left.vec) as (keyof UnitVector)[]) out[key] = (left.vec[key] ?? 0) / k;
        left = { vec: out, scalar: left.scalar };
      }
    }
    return left;
  };

  const addSub = (): Val | null => {
    let left = mulDiv();
    while (left && (tokens[i] === '+' || tokens[i] === '-')) {
      const op = tokens[i++];
      const right = mulDiv();
      if (!right) return null;
      const out: UnitVector = { ...left.vec };
      for (const key of Object.keys(right.vec) as (keyof UnitVector)[]) {
        out[key] = (out[key] ?? 0) + (op === '+' ? 1 : -1) * (right.vec[key] ?? 0);
      }
      left = { vec: out, scalar: left.scalar && right.scalar };
    }
    return left;
  };

  const result = addSub();
  if (!result || i !== tokens.length) return null;
  return result.vec;
}

export function parseDim(input: string): Dim | null {
  const s = input.trim();
  if (s === 'auto') return 'auto';
  if (/^calc\(/i.test(s)) {
    const v = parseCalc(s);
    return v ? vectorToDim(v) : null;
  }
  const v = parseUnitValue(s);
  return v ? vectorToDim(v) : null;
}

function parsePx(input: string): number | null {
  const d = parseDim(input);
  return typeof d === 'number' ? d : null;
}

function parseNum(input: string): number | null {
  const n = Number(input.trim());
  return Number.isFinite(n) ? n : null;
}

/** `asset('content/x.png')` → inner string (path at author time; guid after import rewrite). */
function parseAsset(input: string): string | null {
  const m = /^asset\(\s*['"]?([^'")]+)['"]?\s*\)$/.exec(input.trim());
  return m ? m[1]! : null;
}

/** Split a value into whitespace-separated tokens, keeping `fn(…)` groups intact. */
function splitTokens(value: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of value) {
    if (ch === '(') depth++;
    if (ch === ')') depth = Math.max(0, depth - 1);
    if (/\s/.test(ch) && depth === 0) {
      if (cur) out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur) out.push(cur);
  return out;
}

/** Split on TOP-LEVEL commas only (keeps `rgba(…)` / nested `fn(…)` intact). */
function splitTopLevelCommas(value: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of value) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

/** Expand 1–4 box values (CSS order: t → t r → t r b → t r b l). */
function expandBox<T>(vals: T[]): [T, T, T, T] | null {
  switch (vals.length) {
    case 1:
      return [vals[0]!, vals[0]!, vals[0]!, vals[0]!];
    case 2:
      return [vals[0]!, vals[1]!, vals[0]!, vals[1]!];
    case 3:
      return [vals[0]!, vals[1]!, vals[2]!, vals[1]!];
    case 4:
      return [vals[0]!, vals[1]!, vals[2]!, vals[3]!];
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// property registry

type Patch = Partial<StyleDecls>;
type PropParser = (value: string) => Patch | null;

const enumParser =
  <K extends keyof StyleDecls>(key: K, allowed: readonly string[]): PropParser =>
  (value) => {
    const v = value.trim();
    return allowed.includes(v) ? ({ [key]: v } as Patch) : null;
  };

const dimParser =
  <K extends keyof StyleDecls>(key: K): PropParser =>
  (value) => {
    const d = parseDim(value);
    return d === null ? null : ({ [key]: d } as Patch);
  };

const pxParser =
  <K extends keyof StyleDecls>(key: K): PropParser =>
  (value) => {
    const n = parsePx(value);
    return n === null ? null : ({ [key]: n } as Patch);
  };

const numParser =
  <K extends keyof StyleDecls>(key: K): PropParser =>
  (value) => {
    const n = parseNum(value);
    return n === null ? null : ({ [key]: n } as Patch);
  };

const colorParser =
  <K extends keyof StyleDecls>(key: K): PropParser =>
  (value) => {
    const c = parseColor(value);
    return c === null ? null : ({ [key]: c } as Patch);
  };

const assetParser =
  <K extends keyof StyleDecls>(key: K): PropParser =>
  (value) => {
    const a = parseAsset(value);
    return a === null ? null : ({ [key]: a } as Patch);
  };

/** `font-family: 'Inter', "Noto Sans", sans-serif` → ordered name stack (quotes stripped). */
const fontFamilyParser: PropParser = (value) => {
  const names = splitTopLevelCommas(value)
    .map((n) => n.replace(/^['"]+|['"]+$/g, '').trim())
    .filter((n) => n.length > 0);
  return names.length ? { fontFamily: names } : null;
};

const WEIGHT_KEYWORDS: Record<string, number> = { normal: 400, regular: 400, bold: 700 };
/** `font-weight: bold | 100..1000` → number. `lighter`/`bolder` are relative (unsupported v1 → null). */
const fontWeightParser: PropParser = (value) => {
  const v = value.trim().toLowerCase();
  if (v in WEIGHT_KEYWORDS) return { fontWeight: WEIGHT_KEYWORDS[v]! };
  const n = parseNum(v);
  return n === null || n < 1 || n > 1000 ? null : { fontWeight: n };
};

/** `font-style: normal | italic | oblique[…]` → 'normal' | 'italic' (oblique folds to italic v1). */
const fontStyleParser: PropParser = (value) => {
  const v = value.trim().toLowerCase();
  if (v === 'normal') return { fontStyle: 'normal' };
  if (v === 'italic' || v.startsWith('oblique')) return { fontStyle: 'italic' };
  return null;
};

const boxParser =
  (keys: [keyof StyleDecls, keyof StyleDecls, keyof StyleDecls, keyof StyleDecls]): PropParser =>
  (value) => {
    const dims = splitTokens(value).map(parseDim);
    if (dims.some((d) => d === null)) return null;
    const box = expandBox(dims as Dim[]);
    if (!box) return null;
    return { [keys[0]]: box[0], [keys[1]]: box[1], [keys[2]]: box[2], [keys[3]]: box[3] } as Patch;
  };

/** One `[inset] <x> <y> [blur] [spread] <color>` layer. */
function parseShadowLayer(value: string): ShadowDecl | null {
  const nums: number[] = [];
  let color: Color4 | null = null;
  let inset = false;
  for (const tok of splitTokens(value)) {
    if (tok.toLowerCase() === 'inset') {
      inset = true;
      continue;
    }
    const px = parsePx(tok);
    if (px !== null) {
      if (nums.length >= 4) return null;
      nums.push(px);
      continue;
    }
    const c = parseColor(tok);
    if (c !== null && !color) {
      color = c;
      continue;
    }
    return null;
  }
  if (nums.length < 2 || !color) return null;
  const d: ShadowDecl = { x: nums[0]!, y: nums[1]!, blur: nums[2] ?? 0, spread: nums[3] ?? 0, color };
  if (inset) d.inset = true;
  return d;
}

/** `box-shadow: <layer>, …` — comma list of `[inset] x y [blur] [spread] color`; 'none' clears. */
export function parseShadow(value: string): readonly ShadowDecl[] | null | 'none' {
  if (value.trim() === 'none') return 'none';
  const layers: ShadowDecl[] = [];
  for (const part of splitTopLevelCommas(value)) {
    const layer = parseShadowLayer(part);
    if (!layer) return null;
    layers.push(layer);
  }
  return layers.length ? layers : null;
}

const GRADIENT_MAX_STOPS = 6; // uniform-block budget in ui-gradient.slang — keep in sync

/** Color-stop args → normalized 2..6 stops (pos in % or `deg` for conic; omitted = distributed). */
function buildGradientStops(args: string[]): GradientDecl['stops'] | null {
  const stops: { color: Color4; pos: number | null }[] = [];
  for (const arg of args) {
    const toks = splitTokens(arg);
    if (!toks.length || toks.length > 2) return null;
    const color = parseColor(toks[0]!);
    if (!color) return null;
    let pos: number | null = null;
    if (toks[1] !== undefined) {
      const t = toks[1];
      if (t.endsWith('%')) pos = Number(t.slice(0, -1)) / 100;
      else if (t.endsWith('deg')) pos = Number(t.slice(0, -3)) / 360;
      else return null;
      if (!Number.isFinite(pos)) return null;
    }
    stops.push({ color, pos });
  }
  if (stops.length < 2 || stops.length > GRADIENT_MAX_STOPS) return null;
  // fill omitted positions: endpoints default 0/1, gaps distribute evenly (CSS behavior)
  if (stops[0]!.pos === null) stops[0]!.pos = 0;
  if (stops[stops.length - 1]!.pos === null) stops[stops.length - 1]!.pos = 1;
  for (let i = 1; i < stops.length; i++) {
    if (stops[i]!.pos !== null) continue;
    let j = i;
    while (stops[j]!.pos === null) j++;
    const from = stops[i - 1]!.pos!;
    const to = stops[j]!.pos!;
    for (let k = i; k < j; k++) stops[k]!.pos = from + ((to - from) * (k - i + 1)) / (j - i + 1);
  }
  let run = 0; // enforce ascending (CSS clamps to the running max)
  for (const s of stops) {
    run = Math.max(run, s.pos!);
    s.pos = run;
  }
  return stops.map((s) => ({ color: s.color, pos: s.pos! }));
}

/** `at <pos>` clause → center fraction (reuses transform-origin keyword/% grammar). */
function gradientCenter(clause: string | undefined): { cx: number; cy: number } {
  const o = clause ? parseTransformOrigin(clause) : null;
  return o ? { cx: o.x, cy: o.y } : { cx: 0.5, cy: 0.5 };
}

/** True when `args[0]` is a gradient CONFIG token (shape/from/at), not a color stop. */
function isGradientConfig(arg: string | undefined): boolean {
  return arg !== undefined && parseColor(splitTokens(arg)[0] ?? '') === null;
}

/** `linear|radial|conic-gradient(…)` → GradientDecl (normalized stops). */
export function parseGradient(value: string): GradientDecl | null {
  const s = value.trim();

  const rad = /^radial-gradient\(([\s\S]*)\)$/i.exec(s);
  if (rad) {
    const args = splitTopLevelCommas(rad[1]!);
    let shape: 'circle' | 'ellipse' = 'ellipse';
    let center = { cx: 0.5, cy: 0.5 };
    if (isGradientConfig(args[0])) {
      const cfg = args.shift()!.toLowerCase();
      if (cfg.includes('circle')) shape = 'circle';
      center = gradientCenter(/\bat\s+(.+)$/.exec(cfg)?.[1]);
    }
    const stops = buildGradientStops(args);
    return stops ? { kind: 'radial', angleDeg: 0, cx: center.cx, cy: center.cy, shape, stops } : null;
  }

  const con = /^conic-gradient\(([\s\S]*)\)$/i.exec(s);
  if (con) {
    const args = splitTopLevelCommas(con[1]!);
    let fromDeg = 0;
    let center = { cx: 0.5, cy: 0.5 };
    if (isGradientConfig(args[0])) {
      const cfg = args.shift()!.toLowerCase();
      const from = /\bfrom\s+(-?[\d.]+)deg/.exec(cfg);
      if (from) fromDeg = Number(from[1]);
      center = gradientCenter(/\bat\s+(.+)$/.exec(cfg)?.[1]);
    }
    const stops = buildGradientStops(args);
    return stops ? { kind: 'conic', angleDeg: fromDeg, cx: center.cx, cy: center.cy, stops } : null;
  }

  const m = /^linear-gradient\(([\s\S]*)\)$/i.exec(s);
  if (!m) return null;
  const args = splitTopLevelCommas(m[1]!);
  if (!args.length) return null;
  let angleDeg = 180; // CSS default: to bottom
  const first = args[0]!.toLowerCase();
  const angleMatch = /^(-?[\d.]+)deg$/.exec(first);
  if (angleMatch) {
    angleDeg = Number(angleMatch[1]);
    args.shift();
  } else if (first.startsWith('to ')) {
    const dir = first.slice(3).trim();
    const named: Record<string, number> = { top: 0, right: 90, bottom: 180, left: 270 };
    if (!(dir in named)) return null;
    angleDeg = named[dir]!;
    args.shift();
  }
  const stops = buildGradientStops(args);
  return stops ? { kind: 'linear', angleDeg, stops } : null;
}

/** `<n>deg|rad|turn|grad` (bare = deg) → radians. */
function parseAngle(token: string): number | null {
  const m = /^(-?(?:\d+\.?\d*|\.\d+))(deg|rad|turn|grad)?$/.exec(token.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  switch (m[2]) {
    case 'rad':
      return n;
    case 'turn':
      return n * 2 * Math.PI;
    case 'grad':
      return (n * Math.PI) / 200;
    default:
      return (n * Math.PI) / 180; // deg (and bare)
  }
}

/** Translate axis token → { px, pct(fraction) }; only px/% are valid for translate. */
function parseTranslateAxis(token: string): { px: number; pct: number } | null {
  const v = parseUnitValue(token);
  if (!v) return null;
  if (v.pct !== undefined) return { px: 0, pct: v.pct / 100 };
  if (v.px !== undefined) return { px: v.px, pct: 0 };
  return null; // em/vw/vh not supported in transform v1
}

/** `transform: translate()/scale()/rotate()/skew()…` (ordered) → function list; 'none' clears. */
export function parseTransform(value: string): TransformDecl | null | 'none' {
  const s = value.trim();
  if (s === 'none') return 'none';
  const fns: TransformFn[] = [];
  const re = /([a-zA-Z]+)\(([^)]*)\)/g;
  let m: RegExpExecArray | null;
  let consumed = 0;
  while ((m = re.exec(s)) !== null) {
    if (s.slice(consumed, m.index).trim()) return null; // junk between functions
    consumed = m.index + m[0].length;
    const name = m[1]!.toLowerCase();
    const args = m[2]!
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean);
    switch (name) {
      case 'translate':
      case 'translatex':
      case 'translatey': {
        const ax = parseTranslateAxis(args[0] ?? '');
        if (!ax || args.length > 2) return null;
        let x = { px: 0, pct: 0 };
        let y = { px: 0, pct: 0 };
        if (name === 'translatex') x = ax;
        else if (name === 'translatey') y = ax;
        else {
          x = ax;
          if (args[1] !== undefined) {
            const ay = parseTranslateAxis(args[1]);
            if (!ay) return null;
            y = ay;
          }
        }
        fns.push({ fn: 'translate', x: x.px, y: y.px, xPct: x.pct, yPct: y.pct });
        break;
      }
      case 'scale':
      case 'scalex':
      case 'scaley': {
        const a = parseNum(args[0] ?? '');
        if (a === null || args.length > 2) return null;
        if (name === 'scalex') fns.push({ fn: 'scale', x: a, y: 1 });
        else if (name === 'scaley') fns.push({ fn: 'scale', x: 1, y: a });
        else {
          const b = args[1] !== undefined ? parseNum(args[1]) : a;
          if (b === null) return null;
          fns.push({ fn: 'scale', x: a, y: b });
        }
        break;
      }
      case 'rotate':
      case 'rotatez': {
        const rad = parseAngle(args[0] ?? '');
        if (rad === null || args.length !== 1) return null;
        fns.push({ fn: 'rotate', rad });
        break;
      }
      case 'rotatex':
      case 'rotatey': {
        const rad = parseAngle(args[0] ?? '');
        if (rad === null || args.length !== 1) return null;
        fns.push({ fn: name === 'rotatex' ? 'rotateX' : 'rotateY', rad });
        break;
      }
      case 'translatez': {
        const ax = parseTranslateAxis(args[0] ?? '');
        if (!ax || ax.pct !== 0 || args.length !== 1) return null; // translateZ is px only (no % of depth)
        fns.push({ fn: 'translateZ', z: ax.px });
        break;
      }
      case 'perspective': {
        const v = parseUnitValue(args[0] ?? '');
        if (!v || v.px === undefined || v.px <= 0 || args.length !== 1) return null;
        fns.push({ fn: 'perspective', d: v.px });
        break;
      }
      case 'skew':
      case 'skewx':
      case 'skewy': {
        const a = parseAngle(args[0] ?? '');
        if (a === null || args.length > 2) return null;
        if (name === 'skewx') fns.push({ fn: 'skew', x: a, y: 0 });
        else if (name === 'skewy') fns.push({ fn: 'skew', x: 0, y: a });
        else {
          const b = args[1] !== undefined ? parseAngle(args[1]) : 0;
          if (b === null) return null;
          fns.push({ fn: 'skew', x: a, y: b });
        }
        break;
      }
      default:
        return null;
    }
  }
  if (s.slice(consumed).trim() || fns.length === 0) return null;
  return fns;
}

const ORIGIN_KEYWORDS: Record<string, { axis: 'x' | 'y' | 'any'; v: number }> = {
  left: { axis: 'x', v: 0 },
  right: { axis: 'x', v: 1 },
  top: { axis: 'y', v: 0 },
  bottom: { axis: 'y', v: 1 },
  center: { axis: 'any', v: 0.5 },
};

/** `transform-origin: <x> [y]` — keywords + `%` → fractions (default center). px unsupported (v1). */
export function parseTransformOrigin(value: string): TransformOrigin | null {
  const toks = splitTokens(value.trim().toLowerCase());
  if (toks.length === 0 || toks.length > 2) return null;
  const resolve = (tok: string): { axis: 'x' | 'y' | 'any'; v: number } | null => {
    const kw = ORIGIN_KEYWORDS[tok];
    if (kw) return kw;
    if (tok.endsWith('%')) {
      const n = Number(tok.slice(0, -1));
      return Number.isFinite(n) ? { axis: 'any', v: n / 100 } : null;
    }
    return null;
  };
  let x = 0.5;
  let y = 0.5;
  if (toks.length === 1) {
    const r = resolve(toks[0]!);
    if (!r) return null;
    if (r.axis === 'y') y = r.v;
    else x = r.v; // 'any' or 'x' → x, y stays center
    return { x, y };
  }
  const r0 = resolve(toks[0]!);
  const r1 = resolve(toks[1]!);
  if (!r0 || !r1) return null;
  // keyword axes may swap the pair (`top left`); numbers/any take x-then-y
  if (r0.axis === 'y' || r1.axis === 'x') {
    y = r0.v;
    x = r1.v;
  } else {
    x = r0.v;
    y = r1.v;
  }
  return { x, y };
}

/** kebab transition-property names → StyleDecls keys (subset that interpolate meaningfully). */
const TRANSITION_PROP_ALIASES: Record<string, keyof StyleDecls> = {
  opacity: 'opacity',
  transform: 'transform',
  color: 'color',
  tint: 'tint',
  'background-color': 'backgroundColor',
  'border-color': 'borderColor',
  'border-radius': 'borderRadius',
  'border-width': 'borderWidth',
  'box-shadow': 'shadow',
  width: 'width',
  height: 'height',
  top: 'top',
  right: 'right',
  bottom: 'bottom',
  left: 'left',
  'margin-top': 'marginTop',
  'margin-right': 'marginRight',
  'margin-bottom': 'marginBottom',
  'margin-left': 'marginLeft',
  'padding-top': 'paddingTop',
  'padding-right': 'paddingRight',
  'padding-bottom': 'paddingBottom',
  'padding-left': 'paddingLeft',
};

const EASINGS: readonly EasingName[] = ['linear', 'ease', 'ease-in', 'ease-out', 'ease-in-out'];

/** `<n>s | <n>ms` → milliseconds (bare number treated as seconds — lenient). */
function parseTime(token: string): number | null {
  const m = /^(-?(?:\d+\.?\d*|\.\d+))(ms|s)?$/.exec(token.trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  return m[2] === 'ms' ? n : n * 1000;
}

/** `transition: <prop> <dur> [easing] [delay], …` → list; 'none' clears. */
export function parseTransition(value: string): readonly TransitionDecl[] | null | 'none' {
  const s = value.trim();
  if (s === 'none') return 'none';
  const out: TransitionDecl[] = [];
  for (const entry of s.split(',')) {
    const toks = splitTokens(entry.trim().toLowerCase());
    if (toks.length < 2) return null;
    const propTok = toks[0]!;
    const prop = propTok === 'all' ? 'all' : TRANSITION_PROP_ALIASES[propTok];
    if (!prop) return null;
    const durationMs = parseTime(toks[1]!);
    if (durationMs === null) return null;
    let easing: EasingName = 'ease';
    let delayMs = 0;
    let timeSeen = false;
    for (const tok of toks.slice(2)) {
      if ((EASINGS as readonly string[]).includes(tok)) {
        easing = tok as EasingName;
        continue;
      }
      const t = parseTime(tok);
      if (t !== null && !timeSeen) {
        delayMs = t;
        timeSeen = true;
        continue;
      }
      return null;
    }
    out.push({ prop, durationMs, delayMs, easing });
  }
  return out.length ? out : null;
}

const ANIM_DIRECTIONS: readonly AnimationDirection[] = ['normal', 'reverse', 'alternate', 'alternate-reverse'];
const ANIM_FILLS: readonly AnimationFillMode[] = ['none', 'forwards', 'backwards', 'both'];

/**
 * `animation: <name> <dur> [easing] [delay] [count|infinite] [direction] [fill-mode], …` → list;
 * 'none' clears. CSS shorthand order is loose: the two TIME tokens are duration then delay, a
 * UNITLESS number is the iteration count, and the one token matching no keyword is the keyframes
 * name. Duration + name are required. play-state is not modelled (v1).
 */
export function parseAnimation(value: string): readonly AnimationDecl[] | null | 'none' {
  const s = value.trim();
  if (s.toLowerCase() === 'none') return 'none';
  const out: AnimationDecl[] = [];
  for (const entry of s.split(',')) {
    const toks = splitTokens(entry.trim());
    if (!toks.length) return null;
    let name: string | null = null;
    let durationMs: number | null = null;
    let delayMs = 0;
    let timesSeen = 0;
    let easing: EasingName = 'ease';
    let iterations = 1;
    let infinite = false;
    let direction: AnimationDirection = 'normal';
    let fillMode: AnimationFillMode = 'none';
    for (const raw of toks) {
      const tok = raw.toLowerCase();
      const timeM = /^(-?(?:\d+\.?\d*|\.\d+))(ms|s)$/.exec(tok);
      if (timeM) {
        const ms = tok.endsWith('ms') ? Number(timeM[1]) : Number(timeM[1]) * 1000;
        if (timesSeen === 0) durationMs = ms;
        else if (timesSeen === 1) delayMs = ms;
        else return null;
        timesSeen++;
        continue;
      }
      if (tok === 'infinite') {
        infinite = true;
        continue;
      }
      if (/^\d+\.?\d*$/.test(tok)) {
        iterations = Number(tok); // unitless number = iteration-count (times need s/ms)
        continue;
      }
      if ((EASINGS as readonly string[]).includes(tok)) {
        easing = tok as EasingName;
        continue;
      }
      if ((ANIM_DIRECTIONS as readonly string[]).includes(tok)) {
        direction = tok as AnimationDirection;
        continue;
      }
      if ((ANIM_FILLS as readonly string[]).includes(tok)) {
        if (tok !== 'none') fillMode = tok as AnimationFillMode; // 'none' = the default, no-op
        continue;
      }
      if (name === null) {
        name = raw; // keyframes name — keep author casing
        continue;
      }
      return null; // a second unrecognized token
    }
    if (name === null || durationMs === null) return null;
    out.push({ name, durationMs, delayMs, easing, iterations, infinite, direction, fillMode });
  }
  return out.length ? out : null;
}

/** One `@keyframes` selector (`from` | `to` | `<n>%`, comma list) → normalized offsets 0..1. */
function parseKeyframeSelector(head: string): number[] | null {
  const out: number[] = [];
  for (const part of head.split(',')) {
    const t = part.trim().toLowerCase();
    if (t === 'from') out.push(0);
    else if (t === 'to') out.push(1);
    else if (t.endsWith('%')) {
      const n = Number(t.slice(0, -1));
      if (!Number.isFinite(n)) return null;
      out.push(n / 100);
    } else return null;
  }
  return out.length ? out : null;
}

const PROPS: Record<string, PropParser> = {
  display: enumParser('display', ['flex', 'none']),
  'flex-direction': enumParser('flexDirection', ['row', 'row-reverse', 'column', 'column-reverse']),
  'flex-wrap': enumParser('flexWrap', ['nowrap', 'wrap', 'wrap-reverse']),
  'flex-grow': numParser('flexGrow'),
  'flex-shrink': numParser('flexShrink'),
  'flex-basis': dimParser('flexBasis'),
  flex: (value) => {
    if (value.trim() === 'none') return { flexGrow: 0, flexShrink: 0, flexBasis: 'auto' };
    const toks = splitTokens(value);
    const grow = parseNum(toks[0] ?? '');
    if (grow === null) return null;
    const patch: Patch = { flexGrow: grow };
    if (toks[1] !== undefined) {
      const shrink = parseNum(toks[1]);
      if (shrink === null) return null;
      patch.flexShrink = shrink;
    }
    if (toks[2] !== undefined) {
      const basis = parseDim(toks[2]);
      if (basis === null) return null;
      patch.flexBasis = basis;
    }
    return patch;
  },
  'justify-content': enumParser('justifyContent', [
    'flex-start',
    'center',
    'flex-end',
    'space-between',
    'space-around',
    'space-evenly',
  ]),
  'align-items': enumParser('alignItems', ['auto', 'flex-start', 'center', 'flex-end', 'stretch', 'baseline']),
  'align-self': enumParser('alignSelf', ['auto', 'flex-start', 'center', 'flex-end', 'stretch', 'baseline']),
  'align-content': enumParser('alignContent', ['auto', 'flex-start', 'center', 'flex-end', 'stretch', 'baseline']),
  gap: (value) => {
    const nums = splitTokens(value).map(parsePx);
    if (nums.some((n) => n === null)) return null;
    if (nums.length === 1) return { rowGap: nums[0]!, columnGap: nums[0]! };
    if (nums.length === 2) return { rowGap: nums[0]!, columnGap: nums[1]! };
    return null;
  },
  'row-gap': pxParser('rowGap'),
  'column-gap': pxParser('columnGap'),

  width: dimParser('width'),
  height: dimParser('height'),
  'min-width': dimParser('minWidth'),
  'min-height': dimParser('minHeight'),
  'max-width': dimParser('maxWidth'),
  'max-height': dimParser('maxHeight'),
  margin: boxParser(['marginTop', 'marginRight', 'marginBottom', 'marginLeft']),
  'margin-top': dimParser('marginTop'),
  'margin-right': dimParser('marginRight'),
  'margin-bottom': dimParser('marginBottom'),
  'margin-left': dimParser('marginLeft'),
  padding: boxParser(['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft']),
  'padding-top': dimParser('paddingTop'),
  'padding-right': dimParser('paddingRight'),
  'padding-bottom': dimParser('paddingBottom'),
  'padding-left': dimParser('paddingLeft'),
  position: enumParser('position', ['relative', 'absolute']),
  inset: boxParser(['top', 'right', 'bottom', 'left']),
  top: dimParser('top'),
  right: dimParser('right'),
  bottom: dimParser('bottom'),
  left: dimParser('left'),
  'aspect-ratio': (value) => {
    const m = /^([\d.]+)\s*(?:\/\s*([\d.]+))?$/.exec(value.trim());
    if (!m) return null;
    const a = Number(m[1]);
    const b = m[2] !== undefined ? Number(m[2]) : 1;
    return Number.isFinite(a) && Number.isFinite(b) && b !== 0 ? { aspectRatio: a / b } : null;
  },
  overflow: enumParser('overflow', ['visible', 'hidden', 'scroll']),

  'background-color': colorParser('backgroundColor'),
  'background-image': (value) => {
    const g = parseGradient(value);
    if (g) return { gradient: g };
    const a = parseAsset(value);
    return a === null ? null : { backgroundImage: a };
  },
  // shorthand: one of color / linear-gradient / asset() (no multi-layer backgrounds);
  // 'none' = JSON-safe null sentinels (clear whatever earlier rules set)
  background: (value) => {
    const v = value.trim();
    if (v === 'none') return { backgroundColor: [0, 0, 0, 0], gradient: null, backgroundImage: null };
    const c = parseColor(v);
    if (c) return { backgroundColor: c };
    const g = parseGradient(v);
    if (g) return { gradient: g };
    const a = parseAsset(v);
    return a === null ? null : { backgroundImage: a };
  },
  'box-shadow': (value) => {
    const s = parseShadow(value);
    if (s === null) return null;
    return s === 'none' ? { shadow: null } : { shadow: s };
  },
  '-ui-slice': (value) => {
    const nums = splitTokens(value).map(parsePx);
    if (nums.some((n) => n === null)) return null;
    const box = expandBox(nums as number[]);
    return box ? { slice: box } : null;
  },
  tint: colorParser('tint'),
  opacity: numParser('opacity'),
  '-ui-backdrop-blur': pxParser('backdropBlur'),
  '-ui-backdrop-noise': numParser('backdropNoise'),
  '-ui-material': assetParser('material'),
  'pointer-events': enumParser('pointerEvents', ['auto', 'none']),
  transform: (value) => {
    const t = parseTransform(value);
    if (t === null) return null;
    return t === 'none' ? { transform: null } : { transform: t };
  },
  'transform-origin': (value) => {
    const o = parseTransformOrigin(value);
    return o === null ? null : { transformOrigin: o };
  },
  transition: (value) => {
    const t = parseTransition(value);
    if (t === null) return null;
    return t === 'none' ? { transition: null } : { transition: t };
  },
  animation: (value) => {
    const a = parseAnimation(value);
    if (a === null) return null;
    return a === 'none' ? { animation: null } : { animation: a };
  },
  'border-radius': pxParser('borderRadius'),
  'border-width': pxParser('borderWidth'),
  'border-color': colorParser('borderColor'),
  border: (value) => {
    const patch: Patch = {};
    for (const tok of splitTokens(value)) {
      const px = parsePx(tok);
      if (px !== null) {
        patch.borderWidth = px;
        continue;
      }
      const c = parseColor(tok);
      if (c !== null) {
        patch.borderColor = c;
        continue;
      }
      return null;
    }
    return Object.keys(patch).length ? patch : null;
  },

  color: colorParser('color'),
  font: assetParser('font'),
  'font-family': fontFamilyParser,
  'font-weight': fontWeightParser,
  'font-style': fontStyleParser,
  'font-size': pxParser('fontSize'),
  'line-height': numParser('lineHeight'),
  'text-align': enumParser('textAlign', ['left', 'center', 'right']),
  'letter-spacing': pxParser('letterSpacing'),
  'white-space': enumParser('whiteSpace', ['normal', 'nowrap']),
  'text-overflow': enumParser('textOverflow', ['clip', 'ellipsis']),
};

const kebab = (s: string) => s.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

/** Parse one declaration block body (`prop: value; …`) into typed decls. */
export function parseDecls(
  body: string,
  warn: (message: string, offset: number) => void,
  baseOffset = 0,
): StyleDecls {
  const decls: StyleDecls = {};
  let offset = 0;
  for (const part of body.split(';')) {
    const at = baseOffset + offset;
    offset += part.length + 1;
    const text = part.trim();
    if (!text) continue;
    const colon = text.indexOf(':');
    if (colon < 0) {
      warn(`Malformed declaration '${text}'`, at);
      continue;
    }
    const prop = text.slice(0, colon).trim().toLowerCase();
    const value = text.slice(colon + 1).trim();
    const parser = PROPS[prop];
    if (!parser) {
      warn(`Unsupported property '${prop}'`, at);
      continue;
    }
    const patch = parser(value);
    if (patch === null) {
      warn(`Invalid value '${value}' for '${prop}'`, at);
      continue;
    }
    Object.assign(decls, patch);
  }
  return decls;
}

/** Vue `:style` object / `style="…"` string → typed decls (numbers = px). */
export function parseInlineStyle(input: string | Record<string, unknown> | null | undefined): StyleDecls {
  if (!input) return {};
  if (typeof input === 'string') return parseDecls(input, () => {});
  const decls: StyleDecls = {};
  for (const [rawKey, rawValue] of Object.entries(input)) {
    if (rawValue == null) continue;
    const prop = kebab(rawKey);
    const parser = PROPS[prop];
    if (!parser) continue;
    const value = typeof rawValue === 'number' ? String(rawValue) : String(rawValue);
    const patch = parser(value);
    if (patch) Object.assign(decls, patch);
  }
  return decls;
}

// ---------------------------------------------------------------------------
// stylesheet parser

function lineColAt(source: string, offset: number): { line: number; col: number } {
  let line = 1;
  let last = -1;
  for (let i = 0; i < offset && i < source.length; i++) {
    if (source[i] === '\n') {
      line++;
      last = i;
    }
  }
  return { line, col: offset - last };
}

/** One compound `.a.b:hover` → classes + pseudo; null for unsupported forms. */
function parseCompound(s: string): SelectorCompound | null {
  if (!s.startsWith('.')) return null;
  const classes: string[] = [];
  const pseudo: PseudoState[] = [];
  for (const m of s.matchAll(/([.:])([\w-]+)/g)) {
    if (m.index === undefined) return null;
    if (m[1] === '.') classes.push(m[2]!);
    else {
      if (!(PSEUDO_STATES as readonly string[]).includes(m[2]!)) return null;
      pseudo.push(m[2] as PseudoState);
    }
  }
  // reject anything the token walk didn't consume (e.g. `[attr]`, `>`)
  const consumed = [...s.matchAll(/([.:])([\w-]+)/g)].reduce((n, m) => n + m[0].length, 0);
  if (consumed !== s.length) return null;
  return classes.length ? { classes, pseudo } : null;
}

/** `.a .b.c:hover` → subject compound + descendant-combinator ancestors (outermost first). */
function parseSelector(sel: string): Pick<StyleRule, 'classes' | 'pseudo' | 'ancestors'> | null {
  const parts = sel.trim().split(/\s+/);
  const compounds: SelectorCompound[] = [];
  for (const part of parts) {
    const c = parseCompound(part);
    if (!c) return null;
    compounds.push(c);
  }
  const subject = compounds.pop();
  if (!subject) return null;
  return {
    classes: subject.classes,
    pseudo: subject.pseudo,
    ancestors: compounds.length ? compounds : undefined,
  };
}

/** Parse one `@keyframes` body (`from { … } 50% { … } to { … }`) → a normalized track. */
function parseKeyframesBody(
  name: string,
  body: string,
  baseOffset: number,
  warnAt: (message: string, offset: number, severity?: 'error' | 'warning') => void,
): Keyframes | null {
  const stops: KeyframeStop[] = [];
  const re = /([^{}]+)\{([^{}]*)\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const head = m[1]!;
    const positions = parseKeyframeSelector(head);
    if (!positions) {
      warnAt(`Invalid keyframe selector '${head.trim()}'`, baseOffset + m.index);
      continue;
    }
    const decls = parseDecls(m[2]!, (msg, off) => warnAt(msg, off), baseOffset + m.index + head.length + 1);
    for (const pos of positions) stops.push({ pos, decls });
  }
  if (!stops.length) return null;
  stops.sort((a, b) => a.pos - b.pos);
  const props = [...new Set(stops.flatMap((s) => Object.keys(s.decls)))] as (keyof StyleDecls)[];
  return { name, stops, props };
}

/**
 * Pull `@keyframes name { … }` at-rules out of the source (they nest braces, which the flat rule
 * regex can't handle). Returns the parsed tracks plus a copy of the CSS with each block BLANKED to
 * spaces (offsets/line numbers preserved) so the rule scanner then sees only plain rules.
 */
function extractKeyframes(
  css: string,
  warnAt: (message: string, offset: number, severity?: 'error' | 'warning') => void,
): { keyframes: Record<string, Keyframes>; blanked: string } {
  const head = /@keyframes\s+([A-Za-z_-][\w-]*)\s*\{/gi;
  const keyframes: Record<string, Keyframes> = {};
  let out = '';
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = head.exec(css)) !== null) {
    const bodyStart = m.index + m[0].length;
    let depth = 1;
    let i = bodyStart;
    for (; i < css.length && depth > 0; i++) {
      if (css[i] === '{') depth++;
      else if (css[i] === '}') depth--;
    }
    if (depth !== 0) {
      warnAt(`Unterminated @keyframes '${m[1]}'`, m.index, 'error');
      break;
    }
    const kf = parseKeyframesBody(m[1]!, css.slice(bodyStart, i - 1), bodyStart, warnAt);
    if (kf) keyframes[m[1]!] = kf;
    out += css.slice(last, m.index);
    out += css.slice(m.index, i).replace(/[^\n]/g, ' '); // blank the block, keep newlines
    last = i;
    head.lastIndex = i;
  }
  out += css.slice(last);
  return { keyframes, blanked: out };
}

export function parseStyleSheet(css: string): { sheet: StyleSheetData; diagnostics: UIDiagnostic[] } {
  const diagnostics: UIDiagnostic[] = [];
  const warnAt = (message: string, offset: number, severity: 'error' | 'warning' = 'warning') => {
    const { line, col } = lineColAt(css, offset);
    diagnostics.push({ severity, message, line, col });
  };

  // Strip comments, preserving offsets (replace with spaces).
  const decommented = css.replace(/\/\*[\s\S]*?\*\//g, (m) => m.replace(/[^\n]/g, ' '));
  // Pull @keyframes out first (nested braces) — `stripped` then holds only flat rules.
  const { keyframes, blanked: stripped } = extractKeyframes(decommented, warnAt);

  const rules: StyleRule[] = [];
  const re = /([^{}]+)\{([^{}]*)\}/g;
  let m: RegExpExecArray | null;
  let consumedUntil = 0;
  while ((m = re.exec(stripped)) !== null) {
    const head = m[1]!;
    const body = m[2]!;
    // anything between rules that isn't whitespace is a syntax error
    const gap = stripped.slice(consumedUntil, m.index);
    if (gap.trim()) warnAt(`Unexpected '${gap.trim().slice(0, 24)}'`, consumedUntil, 'error');
    consumedUntil = m.index + m[0].length;

    const bodyOffset = m.index + head.length + 1;
    const decls = parseDecls(body, (msg, off) => warnAt(msg, off), bodyOffset);

    for (const sel of head.split(',')) {
      const parsed = parseSelector(sel);
      if (!parsed) {
        warnAt(
          `Unsupported selector '${sel.trim()}' (class compounds + descendants + :hover/:active/:focus/:disabled)`,
          m.index,
        );
        continue;
      }
      rules.push({ ...parsed, decls });
    }
  }
  const tail = stripped.slice(consumedUntil);
  if (tail.trim()) warnAt(`Unexpected '${tail.trim().slice(0, 24)}'`, consumedUntil, 'error');

  const sheet: StyleSheetData = { rules };
  if (Object.keys(keyframes).length) sheet.keyframes = keyframes;
  return { sheet, diagnostics };
}
